import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput } from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '../design-system';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { FlowNode, FlowCondition, FlowConditionGroup, ConditionField, ConditionOperator } from './flow-types';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/** A plain text HH:MM input, backed by minutes-since-midnight. Keeps its own draft string
 * while typing (synced from the committed value only when it's not being edited) so an
 * in-progress keystroke like "14:" or "14:3" isn't clobbered back to '' by a controlled
 * value round-tripping through a strict HH:MM parser on every character. */
const TimeInput: React.FC<{ placeholder: string; minutes: any; onCommit: (minutes: number | '') => void; size?: 'sm'; error?: string }> = ({
  placeholder,
  minutes,
  onCommit,
  size,
  error,
}) => {
  const format = (m: any) => {
    const n = Number(m);
    if (!Number.isFinite(n) || n < 0) return '';
    const h = Math.floor(n / 60) % 24;
    const mm = Math.floor(n % 60);
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  const [draft, setDraft] = useState(format(minutes));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(format(minutes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes, focused]);

  const parse = (clock: string): number | '' => {
    const match = clock.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return '';
    const h = Math.min(23, Math.max(0, parseInt(match[1], 10) || 0));
    const m = Math.min(59, Math.max(0, parseInt(match[2], 10) || 0));
    return h * 60 + m;
  };

  return (
    <Input
      placeholder={placeholder}
      value={draft}
      onChangeText={(t) => {
        setDraft(t);
        const parsed = parse(t);
        if (parsed !== '') onCommit(parsed);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setDraft(format(minutes));
      }}
      size={size}
      error={error}
    />
  );
};

const FIELD_OPTIONS: Array<{ value: ConditionField; label: string; description: string }> = [
  { value: 'time', label: 'Time of Day', description: 'Pick a clock time (business timezone)' },
  { value: 'day_of_week', label: 'Day of Week', description: 'Pick one or more days' },
  { value: 'contact.isReturning', label: 'Returning Contact', description: 'Has messaged before' },
  { value: 'contact.messageCount', label: 'Message Count', description: 'Total messages from contact' },
  { value: 'contact.hoursSinceLastMessage', label: 'Hours Since Last Message', description: 'Time since last inbound message' },
  { value: 'contact.country', label: 'Country', description: 'Pick from a list, derived from the contact\'s phone number' },
  { value: 'contact.tags', label: 'Contact Tags', description: 'Tags on the contact profile' },
  { value: 'contact.phone', label: 'Contact Phone', description: 'Any of the contact\'s numbers: primary, other identifiers, or the one messaging' },
  { value: 'contact.email', label: 'Contact Email', description: 'Any of the contact\'s emails' },
  { value: 'contact.isAssigned', label: 'Is Assigned', description: 'Contact is assigned to a user/department' },
  { value: 'contact.assignedRole', label: 'Assigned Role', description: 'Role of assigned user/department' },
  { value: 'formula', label: 'Custom Formula', description: 'Math/logic expression, e.g. messageCount > 5 and hoursSinceLastMessage < 24' },
];

// Sentinel picker option — not a real field value, selecting it switches the condition row
// into free-text "custom field path" entry mode instead of setting condition.field directly.
const CUSTOM_FIELD_SENTINEL = '__custom_field__';
const CUSTOM_FIELD_PREFIX = 'contact.profile.';

/** True when a condition's field is a generic dotted path into the contact profile (typed via
 * the custom-field free-text mode) rather than one of the fixed FIELD_OPTIONS values. */
const isCustomFieldPath = (field: string) => field.startsWith(CUSTOM_FIELD_PREFIX);

const COUNTRY_OPTIONS: Array<{ code: string; name: string }> = [
  { code: 'EG', name: 'Egypt' }, { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' },
  { code: 'SA', name: 'Saudi Arabia' }, { code: 'AE', name: 'United Arab Emirates' }, { code: 'KW', name: 'Kuwait' },
  { code: 'QA', name: 'Qatar' }, { code: 'BH', name: 'Bahrain' }, { code: 'OM', name: 'Oman' }, { code: 'JO', name: 'Jordan' },
  { code: 'LB', name: 'Lebanon' }, { code: 'IQ', name: 'Iraq' }, { code: 'SY', name: 'Syria' }, { code: 'YE', name: 'Yemen' },
  { code: 'PS', name: 'Palestine' }, { code: 'MA', name: 'Morocco' }, { code: 'DZ', name: 'Algeria' }, { code: 'TN', name: 'Tunisia' },
  { code: 'LY', name: 'Libya' }, { code: 'SD', name: 'Sudan' }, { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' }, { code: 'ES', name: 'Spain' }, { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' }, { code: 'AT', name: 'Austria' }, { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' }, { code: 'PL', name: 'Poland' }, { code: 'GR', name: 'Greece' },
  { code: 'PT', name: 'Portugal' }, { code: 'IE', name: 'Ireland' }, { code: 'TR', name: 'Turkey' }, { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' }, { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' }, { code: 'CO', name: 'Colombia' }, { code: 'CL', name: 'Chile' }, { code: 'PE', name: 'Peru' },
  { code: 'IN', name: 'India' }, { code: 'PK', name: 'Pakistan' }, { code: 'BD', name: 'Bangladesh' }, { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'ID', name: 'Indonesia' }, { code: 'MY', name: 'Malaysia' },
  { code: 'SG', name: 'Singapore' }, { code: 'PH', name: 'Philippines' }, { code: 'TH', name: 'Thailand' }, { code: 'VN', name: 'Vietnam' },
  { code: 'AU', name: 'Australia' }, { code: 'NZ', name: 'New Zealand' }, { code: 'ZA', name: 'South Africa' }, { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' }, { code: 'GH', name: 'Ghana' }, { code: 'ET', name: 'Ethiopia' },
];

const OPERATOR_OPTIONS: Array<{ value: ConditionOperator; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'between', label: 'Between' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'in', label: 'In List' },
  { value: 'not_in', label: 'Not In List' },
  { value: 'has_value', label: 'Has a Value' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'matches_formula', label: 'Matches Formula' },
];

const emptyCondition = (): FlowCondition => ({ field: 'contact.messageCount', operator: 'greater_than', value: '' });
const emptyGroup = (): FlowConditionGroup => ({ conditions: [emptyCondition()], logic: 'AND' });

interface ConditionGroupsEditorProps {
  node: FlowNode;
  otherNodes: FlowNode[];
  onChange: (patch: Partial<FlowNode>) => void;
  errors: Record<string, string>;
}

export const ConditionGroupsEditor: React.FC<ConditionGroupsEditorProps> = ({ node, otherNodes, onChange, errors }) => {
  const styles = useThemedStyles(getStyles);
  const groups = node.conditionGroups || [];
  const [fieldPickerFor, setFieldPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [operatorPickerFor, setOperatorPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [countryPickerFor, setCountryPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [nodePickerFor, setNodePickerFor] = useState<'default' | number | null>(null);

  const updateGroup = (groupIndex: number, updater: (g: FlowConditionGroup) => FlowConditionGroup) => {
    onChange({ conditionGroups: groups.map((g, i) => (i === groupIndex ? updater(g) : g)) });
  };
  const addGroup = () => onChange({ conditionGroups: [...groups, emptyGroup()] });
  const removeGroup = (groupIndex: number) => onChange({ conditionGroups: groups.filter((_, i) => i !== groupIndex) });
  const addCondition = (groupIndex: number) => updateGroup(groupIndex, (g) => ({ ...g, conditions: [...g.conditions, emptyCondition()] }));
  const removeCondition = (groupIndex: number, conditionIndex: number) =>
    updateGroup(groupIndex, (g) => ({ ...g, conditions: g.conditions.filter((_, i) => i !== conditionIndex) }));
  const updateCondition = (groupIndex: number, conditionIndex: number, patch: Partial<FlowCondition>) =>
    updateGroup(groupIndex, (g) => ({ ...g, conditions: g.conditions.map((c, i) => (i === conditionIndex ? { ...c, ...patch } : c)) }));

  const renderValueInput = (groupIndex: number, conditionIndex: number, condition: FlowCondition) => {
    const errorKey = `group.${groupIndex}.condition.${conditionIndex}`;
    if (condition.operator === 'has_value' || condition.operator === 'is_empty') {
      return null; // these operators don't need a value typed in
    }
    if (condition.field === 'time' && condition.operator === 'between') {
      const [min, max] = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <View style={styles.betweenRow}>
          <View style={styles.betweenInput}><TimeInput placeholder="09:00" minutes={min} onCommit={(v) => updateCondition(groupIndex, conditionIndex, { value: [v, max] })} size="sm" /></View>
          <Text style={styles.betweenSeparator}>and</Text>
          <View style={styles.betweenInput}><TimeInput placeholder="17:00" minutes={max} onCommit={(v) => updateCondition(groupIndex, conditionIndex, { value: [min, v] })} size="sm" /></View>
        </View>
      );
    }
    if (condition.field === 'time') {
      return <TimeInput placeholder="14:30" minutes={condition.value} onCommit={(v) => updateCondition(groupIndex, conditionIndex, { value: v })} size="sm" error={errors[errorKey]} />;
    }
    if (condition.field === 'day_of_week') {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return (
        <View style={styles.dayRow}>
          {days.map((day) => (
            <TouchableOpacity key={day} style={[styles.dayOption, condition.value === day && styles.dayOptionActive]} onPress={() => updateCondition(groupIndex, conditionIndex, { value: day })}>
              <Text style={[styles.dayOptionText, condition.value === day && styles.dayOptionTextActive]}>{day}</Text>
            </TouchableOpacity>
          ))}
        </View>
      );
    }
    if (condition.field === 'contact.country') {
      const selected = COUNTRY_OPTIONS.find((c) => c.code === condition.value);
      return (
        <TouchableOpacity style={styles.selectButton} onPress={() => setCountryPickerFor({ groupIndex, conditionIndex })}>
          <Text style={[styles.selectButtonText, !condition.value && styles.selectButtonPlaceholder]}>{selected ? selected.name : 'Select country'}</Text>
          <Ionicons name="chevron-down" size={20} color={colors.textLight} />
        </TouchableOpacity>
      );
    }
    if (condition.operator === 'between') {
      const [min, max] = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <View style={styles.betweenRow}>
          <View style={styles.betweenInput}><Input placeholder="Min" value={String(min ?? '')} onChangeText={(t) => updateCondition(groupIndex, conditionIndex, { value: [t, max] })} keyboardType="numeric" size="sm" /></View>
          <Text style={styles.betweenSeparator}>and</Text>
          <View style={styles.betweenInput}><Input placeholder="Max" value={String(max ?? '')} onChangeText={(t) => updateCondition(groupIndex, conditionIndex, { value: [min, t] })} keyboardType="numeric" size="sm" /></View>
        </View>
      );
    }
    if (condition.field === 'formula' || condition.operator === 'matches_formula') {
      return <Input placeholder="e.g. messageCount > 5 and hoursSinceLastMessage < 24" value={String(condition.value ?? '')} onChangeText={(t) => updateCondition(groupIndex, conditionIndex, { value: t })} size="sm" error={errors[errorKey]} />;
    }
    if (condition.field === 'contact.isReturning' || condition.field === 'contact.isAssigned') {
      return (
        <View style={styles.booleanRow}>
          {['true', 'false'].map((v) => (
            <TouchableOpacity key={v} style={[styles.booleanOption, String(condition.value) === v && styles.booleanOptionActive]} onPress={() => updateCondition(groupIndex, conditionIndex, { value: v === 'true' })}>
              <Text style={[styles.booleanOptionText, String(condition.value) === v && styles.booleanOptionTextActive]}>{v === 'true' ? 'Yes' : 'No'}</Text>
            </TouchableOpacity>
          ))}
        </View>
      );
    }
    return (
      <Input
        placeholder={condition.operator === 'in' || condition.operator === 'not_in' ? 'comma,separated,values' : 'Value'}
        value={Array.isArray(condition.value) ? condition.value.join(',') : String(condition.value ?? '')}
        onChangeText={(text) => {
          const value = condition.operator === 'in' || condition.operator === 'not_in' ? text.split(',').map((v) => v.trim()).filter(Boolean) : text;
          updateCondition(groupIndex, conditionIndex, { value });
        }}
        keyboardType={condition.field === 'contact.messageCount' || condition.field === 'contact.hoursSinceLastMessage' ? 'numeric' : 'default'}
        size="sm"
        error={errors[errorKey]}
      />
    );
  };

  const renderNextNodeButton = (label: string, value: string | undefined, edgeKey: 'default' | number) => (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.selectButton} onPress={() => setNodePickerFor(edgeKey)}>
        <Text style={[styles.selectButtonText, !value && styles.selectButtonPlaceholder]}>
          {value ? (otherNodes.find((n) => n.id === value)?.name || 'Checker selected') : 'Select checker'}
        </Text>
        <Ionicons name="chevron-down" size={20} color={colors.textLight} />
      </TouchableOpacity>
    </View>
  );

  return (
    <>
      {groups.map((group, groupIndex) => (
        <View key={groupIndex} style={styles.groupCard}>
          <View style={styles.groupHeader}>
            <TextInput
              style={styles.groupNameInput}
              value={group.name || ''}
              onChangeText={(text) => updateGroup(groupIndex, (g) => ({ ...g, name: text.slice(0, 60) || undefined }))}
              placeholder={`Group ${groupIndex + 1} — name this path`}
              placeholderTextColor={colors.textLight as string}
              accessibilityLabel={`Name of group ${groupIndex + 1}`}
            />
            {groups.length > 1 && (
              <TouchableOpacity onPress={() => removeGroup(groupIndex)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.logicToggleRow}>
            <Text style={styles.helperText}>Match</Text>
            {(['AND', 'OR'] as const).map((logic) => (
              <TouchableOpacity key={logic} style={[styles.logicOption, group.logic === logic && styles.logicOptionActive]} onPress={() => updateGroup(groupIndex, (g) => ({ ...g, logic }))}>
                <Text style={[styles.logicOptionText, group.logic === logic && styles.logicOptionTextActive]}>{logic}</Text>
              </TouchableOpacity>
            ))}
            <Text style={styles.helperText}>of the following</Text>
          </View>
          {group.conditions.map((condition, conditionIndex) => {
            const fieldMeta = FIELD_OPTIONS.find((f) => f.value === condition.field);
            const operatorMeta = OPERATOR_OPTIONS.find((o) => o.value === condition.operator);
            return (
              <View key={conditionIndex} style={styles.conditionRow}>
                <View style={styles.conditionRowHeader}>
                  <Text style={styles.conditionIndexLabel}>#{conditionIndex + 1}</Text>
                  {group.conditions.length > 1 && (
                    <TouchableOpacity onPress={() => removeCondition(groupIndex, conditionIndex)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                      <Ionicons name="close-circle" size={18} color={colors.error} />
                    </TouchableOpacity>
                  )}
                </View>
                {isCustomFieldPath(String(condition.field)) ? (
                  <View style={styles.customFieldRow}>
                    <View style={{ flex: 1 }}>
                      <Input
                        placeholder="e.g. email or customFields.birthday"
                        value={String(condition.field).slice(CUSTOM_FIELD_PREFIX.length)}
                        onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { field: `${CUSTOM_FIELD_PREFIX}${text}` })}
                        size="sm"
                        autoCapitalize="none"
                      />
                    </View>
                    <TouchableOpacity
                      style={styles.customFieldChangeButton}
                      onPress={() => setFieldPickerFor({ groupIndex, conditionIndex })}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Ionicons name="swap-horizontal-outline" size={18} color={colors.primary} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity style={styles.selectButton} onPress={() => setFieldPickerFor({ groupIndex, conditionIndex })}>
                    <Text style={styles.selectButtonText}>{fieldMeta?.label || 'Select field'}</Text>
                    <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                  </TouchableOpacity>
                )}
                {condition.field !== 'formula' && (
                  <TouchableOpacity style={[styles.selectButton, styles.conditionSubField]} onPress={() => setOperatorPickerFor({ groupIndex, conditionIndex })}>
                    <Text style={styles.selectButtonText}>{operatorMeta?.label || 'Select operator'}</Text>
                    <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                  </TouchableOpacity>
                )}
                <View style={styles.conditionSubField}>{renderValueInput(groupIndex, conditionIndex, condition)}</View>
              </View>
            );
          })}
          <TouchableOpacity style={styles.addRowButton} onPress={() => addCondition(groupIndex)}>
            <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
            <Text style={styles.addRowButtonText}>Add Condition</Text>
          </TouchableOpacity>
          {renderNextNodeButton('Then Go To', group.nextNodeId, groupIndex)}
        </View>
      ))}
      <TouchableOpacity style={styles.addGroupButton} onPress={addGroup}>
        <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
        <Text style={styles.addGroupButtonText}>Add Condition Group</Text>
      </TouchableOpacity>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Otherwise</Text>
        <Text style={styles.helperText}>Used when no group above matches.</Text>
        {renderNextNodeButton('Go To', node.defaultNextNodeId, 'default')}
      </View>

      {/* Field Picker Modal */}
      <Modal visible={!!fieldPickerFor} animationType="slide" transparent onRequestClose={() => setFieldPickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Field</Text>
              <TouchableOpacity onPress={() => setFieldPickerFor(null)}><Ionicons name="close" size={24} color={colors.textDark} /></TouchableOpacity>
            </View>
            <FlatList
              data={[...FIELD_OPTIONS, { value: CUSTOM_FIELD_SENTINEL, label: 'Custom Contact Field', description: 'Any other contact field, e.g. email or a custom field like customFields.birthday' }]}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.option}
                  onPress={() => {
                    if (fieldPickerFor) {
                      if (item.value === CUSTOM_FIELD_SENTINEL) {
                        updateCondition(fieldPickerFor.groupIndex, fieldPickerFor.conditionIndex, { field: CUSTOM_FIELD_PREFIX, operator: 'has_value', value: '' });
                      } else {
                        const defaultOperator: ConditionOperator = item.value === 'formula' ? 'matches_formula' : 'equals';
                        updateCondition(fieldPickerFor.groupIndex, fieldPickerFor.conditionIndex, { field: item.value, operator: defaultOperator, value: '' });
                      }
                    }
                    setFieldPickerFor(null);
                  }}
                >
                  <Text style={styles.optionLabel}>{item.label}</Text>
                  <Text style={styles.optionDescription}>{item.description}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Operator Picker Modal */}
      <Modal visible={!!operatorPickerFor} animationType="slide" transparent onRequestClose={() => setOperatorPickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Operator</Text>
              <TouchableOpacity onPress={() => setOperatorPickerFor(null)}><Ionicons name="close" size={24} color={colors.textDark} /></TouchableOpacity>
            </View>
            <FlatList
              data={OPERATOR_OPTIONS}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.option}
                  onPress={() => {
                    if (operatorPickerFor) updateCondition(operatorPickerFor.groupIndex, operatorPickerFor.conditionIndex, { operator: item.value, value: '' });
                    setOperatorPickerFor(null);
                  }}
                >
                  <Text style={styles.optionLabel}>{item.label}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Country Picker Modal */}
      <Modal visible={!!countryPickerFor} animationType="slide" transparent onRequestClose={() => { setCountryPickerFor(null); setCountrySearchQuery(''); }}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Country</Text>
              <TouchableOpacity onPress={() => { setCountryPickerFor(null); setCountrySearchQuery(''); }}><Ionicons name="close" size={24} color={colors.textDark} /></TouchableOpacity>
            </View>
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput style={styles.searchInput} placeholder="Search countries..." value={countrySearchQuery} onChangeText={setCountrySearchQuery} placeholderTextColor={colors.textLight} />
            </View>
            <FlatList
              data={COUNTRY_OPTIONS.filter((c) => c.name.toLowerCase().includes(countrySearchQuery.toLowerCase()))}
              keyExtractor={(item) => item.code}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.option}
                  onPress={() => {
                    if (countryPickerFor) updateCondition(countryPickerFor.groupIndex, countryPickerFor.conditionIndex, { value: item.code });
                    setCountryPickerFor(null);
                    setCountrySearchQuery('');
                  }}
                >
                  <Text style={styles.optionLabel}>{item.name}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Next-Node Picker Modal (shared for group edges + default) */}
      <Modal visible={nodePickerFor !== null} animationType="slide" transparent onRequestClose={() => setNodePickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Checker</Text>
              <TouchableOpacity onPress={() => setNodePickerFor(null)}><Ionicons name="close" size={24} color={colors.textDark} /></TouchableOpacity>
            </View>
            <FlatList
              data={otherNodes}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.option}
                  onPress={() => {
                    if (nodePickerFor === 'default') {
                      onChange({ defaultNextNodeId: item.id });
                    } else if (nodePickerFor !== null) {
                      updateGroup(nodePickerFor, (g) => ({ ...g, nextNodeId: item.id }));
                    }
                    setNodePickerFor(null);
                  }}
                >
                  <Text style={styles.optionLabel}>{item.name || 'Untitled checker'}</Text>
                </TouchableOpacity>
              )}
              ListEmptyComponent={<View style={styles.emptyContainer}><Text style={styles.emptyText}>Add another checker first</Text></View>}
            />
          </View>
        </View>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  field: { marginBottom: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: spacing.xs },
  helperText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  errorText: { fontSize: typography.fontSize.xs, color: colors.error, fontFamily: typography.fontFamily.primary, marginTop: spacing.xs / 2 },
  sectionTitle: { fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold, marginBottom: spacing.xs },
  section: { marginTop: spacing.lg, padding: spacing.md, backgroundColor: colors.lightGray, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border },
  groupCard: { marginTop: spacing.md, padding: spacing.md, backgroundColor: colors.lightGray, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border },
  groupHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  groupNameInput: { flex: 1, marginRight: spacing.sm, paddingVertical: 4, fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.bold, borderBottomWidth: 1, borderBottomColor: colors.border, outlineStyle: 'none' as any },
  groupTitle: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  logicToggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md, flexWrap: 'wrap' },
  logicOption: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs / 2, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  logicOptionActive: { borderColor: colors.primary, backgroundColor: `${String(colors.primary)}15` },
  logicOptionText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  logicOptionTextActive: { color: colors.primary },
  conditionRow: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.sm, marginBottom: spacing.sm },
  conditionRowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  conditionIndexLabel: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  conditionSubField: { marginTop: spacing.sm },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.primary },
  selectButtonPlaceholder: { color: colors.textLight },
  customFieldRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  customFieldChangeButton: { padding: spacing.xs },
  betweenRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  betweenInput: { flex: 1 },
  betweenSeparator: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  booleanRow: { flexDirection: 'row', gap: spacing.sm },
  booleanOption: { flex: 1, padding: spacing.sm, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.surface, alignItems: 'center' },
  booleanOptionActive: { borderColor: colors.primary, backgroundColor: `${String(colors.primary)}15` },
  booleanOptionText: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  booleanOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  dayRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  dayOption: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.background.surface, minWidth: 44, alignItems: 'center' },
  dayOptionActive: { borderColor: colors.primary, backgroundColor: `${String(colors.primary)}15` },
  dayOptionText: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  dayOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  addRowButton: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs },
  addRowButtonText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  addGroupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: designTokens.borderRadius.md,
    borderStyle: 'dashed',
  },
  addGroupButtonText: { fontSize: typography.fontSize.base, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  modalTitle: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  searchContainer: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border, backgroundColor: colors.background.surface },
  searchIcon: { marginRight: spacing.sm },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.primary, paddingVertical: spacing.xs },
  option: { padding: spacing.md, borderBottomWidth: 1, borderBottomColor: `${String(colors.border)}40` },
  optionLabel: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: spacing.xs / 2 },
  optionDescription: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  emptyContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary },
});
